import {
  Cancel,
  Edit,
  LabelImportant,
  Search,
  Undo,
} from '@mui/icons-material';
import CurrencyExchange from '@mui/icons-material/CurrencyExchange';
import FreeBreakfast from '@mui/icons-material/FreeBreakfast';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import LinkIcon from '@mui/icons-material/Link';
import MonetizationOn from '@mui/icons-material/MonetizationOn';
import OpenInNew from '@mui/icons-material/OpenInNew';
import Refresh from '@mui/icons-material/Refresh';
import {
  Avatar,
  Box,
  ButtonGroup,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
  colors,
  type GridProps,
} from '@mui/material';

import React, { useMemo } from 'react';
import { Button } from '~/components/Button';
import { Link } from '~/components/Link';
import { ReportHistoryTimeline } from '~/components/ReportHistoryTimeline';
import { ReportImage } from '~/components/ReportImage';
import { ReportName, ReportNameTypography } from '~/components/ReportName';
import { ShareReportButton } from '~/components/ShareReportButton';
import useFeature from '~/hooks/useFeature';
import useRouter from '~/hooks/useRouter';
import { useScreenSize } from '~/hooks/useScreenSize';
import { useCategoryIdMap } from '~/providers/RootDataProvider';
import { cleanReportName } from '~/scripts/cleanReportName';
import { isEmptySearch } from '~/scripts/isEmptySearch';
import parseDomain from '~/scripts/parseDomain';
import type { Reports, Searches, Suggestions } from '~/types';
interface ReportPageHeaderProps extends GridProps {
  loading: boolean;
  name: string;
  logo: string;
  reportDetails?: Reports.ReportDetails | undefined;
  uuid: string | undefined;
  search: Searches.SearchObject | undefined | null;
  report: Suggestions.Suggestion | undefined;
  total: number;
  error: boolean;
  history?: Reports.ReportHistory[];
  actions: React.ReactNode;
  onDiscardChangesClick: () => void;
  onUpdateClick: () => void;
  isDirty: boolean;
  temporarySearch: Searches.SearchObject | undefined | null;
  setIsEditingSearch: (isEditing: boolean) => void;
  partial_results?: boolean;
}
export default function ReportPageHeader({
  loading,
  name,
  logo,
  report,
  uuid,
  search,
  total,
  error,
  history,
  reportDetails,
  onDiscardChangesClick,
  onUpdateClick,
  actions,
  temporarySearch,
  setIsEditingSearch,
  isDirty,
  partial_results,
  ...gridProps
}: ReportPageHeaderProps) {
  const [showHistoryTimeline] = useFeature('report_history_timeline');

  const router = useRouter();
  const isEmpty = useMemo(
    () => isEmptySearch(temporarySearch),
    [temporarySearch]
  );
  const isNewReport = useMemo(() => !uuid, [uuid]);

  const { lgAndUp, mdAndDown } = useScreenSize();
  return (
    <Grid container py={0} className="page-header" {...gridProps}>
      {
        <Grid
          item
          xs={12}
          md={12}
          lg={'auto'}
          alignContent={'center'}
          sx={{
            justifyItems: {
              xs: 'center',
              lg: 'flex-start',
            },
            py: {
              xs: 2,
              sm: 0,
            },
          }}
          px={2}>
          <Avatar
            variant="rounded"
            sx={{
              backgroundColor: colors.grey[50],
              m: 1,
              height: 80,
              width: 80,
              border: `solid 1px ${colors.grey[200]}`,
            }}>
            {loading ? (
              <CircularProgress size={32} />
            ) : (
              <ReportImage
                uuid={logo}
                height={80}
                width={80}
                sx={{ m: 1 }}
                alt={`${name} logo`}
              />
            )}
          </Avatar>
        </Grid>
      }
      <Grid
        item
        xs={12}
        md={12}
        lg={true}
        alignContent={'center'}
        sx={{
          textAlign: {
            xs: 'center',
            lg: 'left',
          },
          py: report || reportDetails ? 3 : 2,
          pr: 1,
        }}
        // sx={{ backgroundColor: purple[50] }}>
      >
        <ReportDetails
          loading={!!uuid && !search}
          search={search}
          total={total}
          report={report}
          partial_results={!!partial_results}
          reportDetails={reportDetails}
        />
      </Grid>
      <Grid
        item
        // sx={{ backgroundColor: orange[50] }}
        xs={12}
        lg={true}
        sx={{
          alignContent: 'center',
          textAlign: {
            xs: 'center',
            lg: 'right',
          },
          pb: {
            xs: 4,
            lg: 0,
          },
        }}
        px={2}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="flex-end"
          flexDirection={mdAndDown ? 'column' : 'row'}>
          {history && !loading && showHistoryTimeline && (
            <Box
              mr={3}
              mt={1}
              sx={{
                border: '1px solid rgba(0,0,0,0.12)',
                borderRadius: 2,
                overflow: 'hidden',
              }}>
              <ReportHistoryTimeline
                history={history}
                compact
                LineChartProps={{
                  height: 60,
                  margin: { top: 0, right: 0, bottom: -25, left: -45 },
                }}
              />
            </Box>
          )}
          {uuid && total > 0 && !isDirty && !loading && (
            <ShareReportButton
              uuid={uuid}
              reportTitle={name}
              fullWidth={mdAndDown}
              sx={{ mr: 1, mb: mdAndDown ? 1 : 0 }}
            />
          )}
          <ButtonGroup
            orientation={mdAndDown ? 'vertical' : 'horizontal'}
            variant="outlined"
            size={mdAndDown ? 'large' : undefined}
            fullWidth={mdAndDown}
            color="primary"
            sx={{ mr: 1 }}>
            {!lgAndUp && !isNewReport && (
              <Button
                onClick={() => setIsEditingSearch(true)}
                disabled={loading}
                startIcon={<Edit />}>
                Edit Search
              </Button>
            )}
            {isDirty && !loading && (
              <Button startIcon={<Undo />} onClick={onDiscardChangesClick}>
                Undo Changes
              </Button>
            )}
            {isDirty && !isEmpty && (
              <Button
                disabled={isEmpty}
                sx={{ px: 4 }}
                variant="contained"
                startIcon={<Search />}
                onClick={onUpdateClick}>
                Search
              </Button>
            )}

            {loading && (
              <Button
                variant="outlined"
                color="warning"
                startIcon={<Cancel />}
                onClick={() => {
                  window.dispatchEvent(new Event('abortSearch'));
                }}>
                Cancel
              </Button>
            )}
            {total > 0 && !isDirty && !loading && actions}
            {error && !isDirty && (
              <Button
                variant="contained"
                color="warning"
                sx={{ px: 4 }}
                startIcon={<Refresh />}
                onClick={() => {
                  router.reload();
                }}>
                Retry Search
              </Button>
            )}
          </ButtonGroup>
        </Box>
      </Grid>
      <Grid item xs={12}>
        <Divider />
      </Grid>
    </Grid>
  );
}

const ReportDetails = ({
  search,
  total,
  report,
  reportDetails,
  partial_results,
  loading,
}: {
  search: Searches.StoredSearchObject | undefined | null;
  total: number;
  report: Suggestions.Suggestion | undefined;
  reportDetails: Reports.ReportDetails | undefined;
  loading: boolean;
  partial_results: boolean;
}) => {
  const categoryIdMap = useCategoryIdMap();
  const categories: Suggestions.Category[] = useMemo(() => {
    if (!report) {
      return [];
    }
    if (report.categories_ids) {
      return report.categories_ids
        .map((categoryId) => categoryIdMap.get(categoryId))
        .filter(Boolean) as Suggestions.Category[];
    }
    if (report.categories_id) {
      const category = categoryIdMap.get(report.categories_id);
      if (category) {
        return [category];
      }
    }
    return [];
  }, [report, categoryIdMap]);

  const parsedDomain = useMemo(() => {
    try {
      return parseDomain(reportDetails?.link || '');
    } catch {
      return '';
    }
  }, [reportDetails?.link]);

  if (loading) {
    return (
      <>
        <Skeleton variant="text" animation="wave" width={600} height={28} />
        <Skeleton variant="text" animation="wave" width={400} height={27} />
        <Skeleton variant="text" animation="wave" width={200} height={27} />
      </>
    );
  }

  return (
    <>
      {search ? (
        <>
          <Typography variant="h4" component="h1" display={'inline'}>
            {partial_results && (
              <Tooltip title="We couldn't load all the results at once. Refresh the page to try this search again.">
                <InfoOutlined
                  fontSize="medium"
                  color="warning"
                  sx={{ verticalAlign: 'middle', mr: 0.5 }}
                />
              </Tooltip>
            )}
            {total ? total.toLocaleString() : ''} Websites using{' '}
            <ReportName
              search={search}
              TypographyProps={{
                variant: 'h4',
                component: 'span',
              }}
            />
          </Typography>
        </>
      ) : (
        <ReportNameTypography variant="h4">
          New Technology List
        </ReportNameTypography>
      )}

      <Box mt={1}>
        {!search && (
          <Typography variant="h6">
            Find websites that use any technology, HTML, or Javascript.
          </Typography>
        )}
        <Stack direction="row" gap={1} flexWrap="wrap">
          {parsedDomain && reportDetails?.link && (
            <Chip
              component="a"
              href={`${reportDetails.link}?&utm_source=www.nerdydata.com`}
              target="_blank"
              color="primary"
              variant="outlined"
              rel="noopener noreferrer"
              icon={<LinkIcon />}
              deleteIcon={<OpenInNew />}
              onDelete={() => {}}
              size="small"
              label={parsedDomain}
            />
          )}
          {categories.map((category) => {
            return (
              <Chip
                key={category.id}
                component={Link}
                href={`/technologies/${cleanReportName(category.name)}/${
                  category.id
                }`}
                color="info"
                size="small"
                variant="outlined"
                icon={<LabelImportant />}
                label={category.name}
              />
            );
          })}
          {reportDetails?.spend && (
            <>
              {reportDetails.spend.free && (
                <Chip
                  color="info"
                  variant="outlined"
                  icon={<FreeBreakfast />}
                  label="Free Software"
                  size="small"
                />
              )}
              {reportDetails.spend.freemium && (
                <Chip
                  color="warning"
                  variant="outlined"
                  icon={<CurrencyExchange />}
                  label="Freemium Software"
                  size="small"
                />
              )}
              {!reportDetails.spend.free && !reportDetails.spend.freemium && (
                <Chip
                  color="success"
                  variant="outlined"
                  icon={<MonetizationOn />}
                  label="Paid Software"
                  size="small"
                />
              )}
            </>
          )}
        </Stack>
      </Box>
    </>
  );
};
